import * as z from "zod";

/**
 * How a value reads: `currency` is USD dollars whatever the store sells in
 * (see `native` for the recorded currency), `percent` is a fraction (0.02 =
 * 2%), and `count` is a plain number.
 */
export const homeMetricFormats = ["currency", "percent", "count"] as const;

/**
 * An amount in the currency Redo recorded it in, which for most merchants is
 * the store's own. Carried beside the USD figure so a page can show the real
 * amount rather than one converted at today's rate.
 */
const NativeAmountSchema_ = z.object({
  amount: z.number(),
  currency: z.string(),
});

const HomeMetricSchema_ = z.object({
  /**
   * Identifies the metric within its product, e.g. "returnRate" under Returns.
   * Scoped rather than global, so a name only has to be unique where it means
   * something. Stable: it survives a change to the formula behind it.
   */
  type: z.string(),
  /** English fallback; the client translates by `type`. */
  label: z.string(),
  format: z.enum(homeMetricFormats),
  value: z.number(),
  /** Same metric for the prior month, when that month has data. */
  previousValue: z.number().nullable(),
  /** A modelled proxy rather than a measurement, so the client can say so. */
  estimated: z.boolean(),
  /**
   * The count the source reports behind the figure, usually events (e.g.
   * returns), so a merchant can check the amount against something they can
   * count. Null when the source reports none.
   */
  count: z.number().nullable(),
  /**
   * Null for figures that aren't money, and when the source doesn't record one
   * currency and amount on every row (e.g. report-based averages).
   */
  native: NativeAmountSchema_.nullable(),
});

export const HomeMetricSchema: z.ZodType<HomeMetricSchema> = HomeMetricSchema_;
export interface HomeMetricSchema extends z.infer<typeof HomeMetricSchema_> {}

const HomeProductMetricsSchema_ = z.object({
  /**
   * The Redo product that earns these figures, as the warehouse labels it —
   * not an enum we maintain, so a product we never enumerated still appears.
   */
  product: z.string(),
  /**
   * The cloud the product belongs to, for surfaces that group that way. Empty
   * when no source names one.
   */
  productCloud: z.string(),
  /** Never empty: a product with nothing to show is left out entirely. */
  metrics: z.array(HomeMetricSchema),
});

export const HomeProductMetricsSchema: z.ZodType<HomeProductMetricsSchema> =
  HomeProductMetricsSchema_;
export interface HomeProductMetricsSchema extends z.infer<
  typeof HomeProductMetricsSchema_
> {}

export const getHomeMetrics = {
  name: "getHomeMetrics" as const,
  input: z.null(),
  output: z.object({
    /** The month the figures cover, as YYYY-MM, for locale formatting. */
    periodKey: z.string(),
    /** English fallback for `periodKey`, e.g. "July 2026". */
    periodLabel: z.string(),
    /**
     * Grouped by product, so a figure is reached by product and then type
     * rather than by scanning one flat pool. Only products this merchant
     * actually has data for, in the order the registry declares them.
     */
    products: z.array(HomeProductMetricsSchema),
    /**
     * Value from Redo: the merchant value model's total across the products
     * the merchant holds, in USD, including revenue attributed to Redo
     * touchpoints (campaigns, popups, tracking pages). Sums the same figures
     * as the cards. Absent when it nets to nothing this month.
     */
    heroRevenue: z
      .object({
        value: z.number(),
        /** Null when nothing counted toward it last month. */
        previousValue: z.number().nullable(),
        /** The part of `value` that is modelled rather than measured. */
        estimatedValue: z.number(),
        /**
         * The same headline in the currency its parts were recorded in. Absent
         * unless every part shares one: mixed currencies can't be summed
         * without a conversion, so the client converts `value` instead.
         */
        native: z
          .object({
            value: z.number(),
            previousValue: z.number().nullable(),
            currency: z.string(),
          })
          .optional(),
      })
      .optional(),
  }),
};
